import { useEffect, useState } from 'react';
import type { DesktopUpdateState } from '@estudio/desktop-contract';
import { Link } from 'react-router';
import { Button, Card } from '../ui/index.tsx';

function useDesktopUpdates() {
  const api = window.__ESTUDIO_DESKTOP__?.updates;
  const [state, setState] = useState<DesktopUpdateState | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!api) return;
    let active = true;
    const refresh = () => { void api.status().then((s) => { if (active) setState(s); }).catch(() => { if (active) setError('No se pudo consultar el actualizador. Reinicia PromptCut para reintentar.'); }); };
    refresh(); const timer = setInterval(refresh, 3000);
    return () => { active = false; clearInterval(timer); };
  }, [api]);
  const run = async (action: 'check' | 'download' | 'install') => {
    setError('');
    try { await api?.[action](); if (api) setState(await api.status()); }
    catch (e) { setError(e instanceof Error ? e.message : 'No se completó la operación.'); }
  };
  return { api, state, error, run };
}

export function DesktopUpdateNotice() {
  const { state } = useDesktopUpdates();
  if (!state || !['available', 'downloading', 'ready'].includes(state.phase)) return null;
  return <div className="banner" role="status">{state.phase === 'ready' ? 'Actualización lista: se instalará al cerrar, cuando no haya trabajos ni guardados pendientes' : state.phase === 'downloading' ? `Descargando actualización: ${state.percent ?? 0} %` : `Nueva versión ${state.version} disponible`}. <Link to="/ajustes">Ver actualización</Link></div>;
}

export function DesktopUpdateSettings() {
  const { api, state, error, run } = useDesktopUpdates();
  if (!api) return null;
  const labels = { idle: 'Pendiente de comprobar', checking: 'Buscando actualizaciones…', current: 'No hay una versión publicada más reciente',
    available: 'Actualización disponible', downloading: 'Descargando', ready: 'Lista para instalar', error: 'No se pudo actualizar', disabled: 'Compilación de desarrollo: actualizaciones desactivadas' };
  return <Card title="Actualizaciones de PromptCut"><div className="stack">
    <p>Versión instalada: <strong>{state?.currentVersion ?? '…'}</strong></p>
    <p role="status">{state ? labels[state.phase] : 'Consultando…'}{state?.version ? ` · ${state.version}` : ''}{state?.phase === 'downloading' ? ` · ${state.percent ?? 0} %` : ''}</p>
    <p className="muted">Se buscan versiones al abrir y cada seis horas, y se descargan automáticamente. Al cerrar, la actualización se instala si no hay trabajos ni guardados pendientes. También puedes instalarla y reiniciar ahora.</p>
    {state?.checkedAt && <p className="muted">Última comprobación: {new Date(state.checkedAt).toLocaleString()}</p>}
    {(error || state?.message) && <p role="alert">{error || state?.message}</p>}
    <Button disabled={!state || ['checking', 'downloading', 'ready', 'disabled'].includes(state.phase)} onClick={() => void run('check')}>Buscar actualizaciones</Button>
    {state?.phase === 'available' && <Button onClick={() => void run('download')}>Descargar actualización</Button>}
    {state?.phase === 'ready' && <Button onClick={() => void run('install')}>Instalar y reiniciar</Button>}
  </div></Card>;
}
